import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import "./AIVisualGuide.css";

export default function AIVisualGuide({ guide }) {
  const { t } = useTranslation("ai");
  const [index, setIndex] = useState(guide.startStep);
  const [expanded, setExpanded] = useState(false);
  const [failedImages, setFailedImages] = useState({});
  // Use the message's language, matching the stored guide rather than a later UI change.
  const label = (key, options = {}) => t(`visualGuide.${key}`, { lng: guide.languageCode, ...options });
  const step = guide.steps[index];
  const imageFailed = failedImages[step.imageUrl];
  const audienceLabel = {
    "approved-promoter": "audienceApprovedPromoter",
    "user": "audienceUser",
    "user-host-promoter": "audienceUserHostPromoter",
    "user-with-business-account": "audienceUserWithBusinessAccount",
    "host-promoter": "audienceHostPromoter",
  }[guide.audience] || "audience";
  return (
    <section className={`pia-visual-guide${expanded ? " pia-visual-guide--expanded" : ""}`} aria-label={guide.title}>
      <div className="pia-visual-guide__top">
        <span>{label("badge")}</span>
        <button type="button" onClick={() => setExpanded((v) => !v)} aria-expanded={expanded}>
          {label(expanded ? "collapse" : "expand")}
        </button>
      </div>
      <h3>{guide.title}</h3>
      <p className="pia-visual-guide__scope">{label(audienceLabel)}</p>
      {guide.languageCode !== guide.screenshotLanguageCode && <p className="pia-visual-guide__scope">{label("englishScreens")}</p>}
      <div className="pia-visual-guide__progress" role="group" aria-label={label("steps")}>
        {guide.steps.map((item, i) => (
          <button key={item.id} type="button" aria-current={i === index ? "step" : undefined}
            aria-label={label("jump", { number: i + 1, title: item.title })}
            className={i <= index ? "pia-visual-guide__visited" : ""} onClick={() => setIndex(i)} />
        ))}
      </div>
      <div aria-live="polite" aria-atomic="true">
        <div className="pia-visual-guide__count">{label("step", { number: index + 1, total: guide.steps.length })}</div>
        <h4>{step.title}</h4>
        <p>{step.instruction}</p>
      </div>
      {imageFailed ? (
        <div className="pia-visual-guide__unavailable" role="status">
          <p>{label("imageUnavailable")}</p>
          <button type="button" onClick={() => setFailedImages((v) => ({ ...v, [step.imageUrl]: false }))}>{label("retry")}</button>
        </div>
      ) : (
        <div className="pia-visual-guide__screen">
          <img key={step.imageUrl} src={step.imageUrl} alt={step.title} loading="lazy"
            onError={() => setFailedImages((v) => ({ ...v, [step.imageUrl]: true }))} />
          {step.highlight && <div aria-hidden="true" className="pia-visual-guide__highlight" style={{
            left: `${step.highlight[0]}%`, top: `${step.highlight[1]}%`, width: `${step.highlight[2]}%`, height: `${step.highlight[3]}%`,
          }}><span>{index + 1}</span></div>}
        </div>
      )}
      <p className="pia-visual-guide__note">{step.note}</p>
      <nav className="pia-visual-guide__navigation" aria-label={label("navigation")}>
        <button type="button" disabled={index === 0} onClick={() => setIndex((v) => v - 1)}>{label("previous")}</button>
        <span>{index + 1} / {guide.steps.length}</span>
        <button type="button" className="pia-visual-guide__next" onClick={() => setIndex((v) => (v + 1) % guide.steps.length)}>
          {label(index === guide.steps.length - 1 ? "restart" : "next")}
        </button>
      </nav>
    </section>
  );
}
